import {
  BodyMedium,
  BodySmall,
  CardBody,
  Checkbox,
  HeadingMedium,
  HeadingSmall,
  Switch,
  ToggleButton,
  theme,
  useToast,
} from '@arnold/common';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Features,
  NotificationLevel,
  useDisableFeatureMutation,
  useEnableFeatureMutation,
  useUpdateOrganizationMutation,
} from '../../generated/hooks';

const StyledFeature = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.f} 0;
  align-items: flex-start;
`;

const ActionItemContainer = styled.div`
  align-items: self-end;
  width: 420px;
  margin-left: ${theme.spacing.f};
`;

const ToggleContainer = styled(ActionItemContainer)`
  display: flex;
  justify-content: flex-end;
`;

interface IProps {
  features: Array<Features | null>;
  organizationId: string;
  organizationName: string;
  processNotificationsLocked: boolean;
  processNotificationsDefault: NotificationLevel;
}

interface IPropsFeature {
  feature: Features;
  enabled: boolean;
  organizationId: string;
}

const FeatureToggle = ({ feature, enabled, organizationId }: IPropsFeature) => {
  const [t] = useTranslation('notificationSettings');
  const [checked, setChecked] = useState(enabled);
  const [enableFeature] = useEnableFeatureMutation();
  const [disableFeature] = useDisableFeatureMutation();
  const { addToast } = useToast();

  const refetchQueries = ['OrganizationSetting'];

  const handleChange = async (value: boolean) => {
    setChecked(value);
    if (value) {
      try {
        await enableFeature({
          variables: {
            feature,
            organizationId,
          },
          refetchQueries,
          awaitRefetchQueries: true,
        });
        addToast(t('switchedAutoApproveOn'));
      } catch {
        setChecked(false);
      }
    } else {
      try {
        await disableFeature({
          variables: {
            feature,
            organizationId,
          },
          refetchQueries,
          awaitRefetchQueries: true,
        });
        addToast(t('switchedAutoApproveOff'));
      } catch {
        setChecked(true);
      }
    }
  };

  return (
    <StyledFeature>
      <div>
        <BodyMedium margin={`0`} data-icom={`feature-title-${feature}`}>
          {t(`feature-title-${feature}`)}
        </BodyMedium>
        <BodySmall color={`${theme.colors.text.secondary}`}>{t(`feature-description-${feature}`)}</BodySmall>
      </div>
      <ToggleContainer data-cy={`admin-feature-${feature}`}>
        <ToggleButton value={checked} onChange={handleChange} />
      </ToggleContainer>
    </StyledFeature>
  );
};

interface IPropsProcessNotificationsSettings {
  defaultValue: NotificationLevel;
  defaultLockSetting: boolean;
  organizationId: string;
}

const ProcessNotificationsSettings = ({
  defaultValue,
  defaultLockSetting,
  organizationId,
}: IPropsProcessNotificationsSettings) => {
  const [t] = useTranslation('notificationSettings');
  const [value, setValue] = useState<NotificationLevel>(defaultValue);
  const [enabled, setEnabled] = useState<boolean>(!defaultLockSetting);
  const [updateOrganization] = useUpdateOrganizationMutation();
  const { addToast } = useToast();
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    updateOrganization({
      variables: {
        input: {
          id: organizationId,
          processNotificationsDefault: value,
          processNotificationsLocked: !enabled,
        },
      },
      refetchQueries: ['OrganizationSetting'],
    })
      .then(() => {
        addToast(t('notificationSettingsSaved'));
      })
      .catch(() => {
        addToast(t('notificationSettingsError'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled]);
  return (
    <StyledFeature>
      <div>
        <BodyMedium margin={`0`} data-icom={`feature-title-`}>
          {t('sendNotificationsAboutNewMessages')}
        </BodyMedium>
        <BodySmall color={`${theme.colors.text.secondary}`}>
          {t('sendNotificationsAboutNewMessagesDescription')}
        </BodySmall>
      </div>
      <ActionItemContainer>
        <Switch
          value={value}
          onChange={(newVal) => setValue(newVal)}
          possibleValues={[
            { value: NotificationLevel.Never, label: t('notificationsNever') },
            { value: NotificationLevel.AlertsOnly, label: t('notificationsAlertsOnly') },
            { value: NotificationLevel.All, label: t('notificationsEvery') },
          ]}
        />
        <div className="mt-6">
          <Checkbox
            selected={enabled}
            onToggle={() => {
              setEnabled(!enabled);
            }}
            text={t('lockOption')}
          />
        </div>
      </ActionItemContainer>
    </StyledFeature>
  );
};

const StyledHr = styled.hr`
  background-color: ${theme.colors.borderMain.default};
  margin-top: ${theme.spacing.h};
  margin-bottom: ${theme.spacing.h};
`;

const NotificationCard = ({
  features,
  organizationId,
  processNotificationsDefault,
  processNotificationsLocked,
}: IProps) => {
  const [t] = useTranslation('notificationSettings');
  return (
    <Card data-cy="admin-notification-card" data-icom="admin-notification-card">
      <CardBody>
        <HeadingMedium margin={`0 0 ${theme.spacing.c} 0`}>{t('title')}</HeadingMedium>
        <BodyMedium margin={`0 0 ${theme.spacing.h}`}>{t('description')}</BodyMedium>
        <div data-icom="notification-settings-standalone-surveys">
          <HeadingSmall>{t('standaloneSurveysTitle')}</HeadingSmall>
          <FeatureToggle
            feature={Features.AutomaticApprovedStandaloneReports}
            enabled={features.includes(Features.AutomaticApprovedStandaloneReports)}
            organizationId={organizationId}
          />
        </div>
        <StyledHr />
        <div data-icom="notification-settings-process-surveys">
          <HeadingSmall>{t('processSurveysTitle')}</HeadingSmall>
          <FeatureToggle
            feature={Features.AutomaticApprovedProcessReports}
            enabled={features.includes(Features.AutomaticApprovedProcessReports)}
            organizationId={organizationId}
          />
          <ProcessNotificationsSettings
            defaultValue={processNotificationsDefault}
            defaultLockSetting={processNotificationsLocked}
            organizationId={organizationId}
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default NotificationCard;
